import { sendExperimentViewEvent } from '@/analytics/ga';

import { AB_TEST_DATA_KEY, SESSION_DATA_KEY, SESSION_TIMEOUT_MS } from './config';
import type { ABTest } from './types';

interface SessionData {
    experimentViewSent: boolean;
    timestamp: number;
}

function getSessionData(): SessionData | null {
    try {
        const data = localStorage.getItem(SESSION_DATA_KEY);
        if (!data) return null;
        return JSON.parse(data) as SessionData;
    } catch (error) {
        console.error('Error reading session data:', error);
        return null;
    }
}

function isSessionValid(sessionData: SessionData | null): boolean {
    if (!sessionData) return false;

    const now = Date.now();
    const timeSinceCreation = now - sessionData.timestamp;

    return sessionData.experimentViewSent && timeSinceCreation < SESSION_TIMEOUT_MS;
}

function experimentViewSent(): boolean {
    const sessionData = getSessionData();
    return isSessionValid(sessionData);
}

function saveSessionData(experimentViewSent: boolean): void {
    const sessionData: SessionData = {
        experimentViewSent,
        timestamp: Date.now(),
    };

    try {
        localStorage.setItem(SESSION_DATA_KEY, JSON.stringify(sessionData));
    } catch (error) {
        console.error('Error saving session data:', error);
    }
}

async function fetchABTests(): Promise<ABTest[] | undefined> {
    try {
        const response = await fetch('/api/ab-test/tracking');
        const data = (await response.json()) as { tracking_data: ABTest[] };

        return data.tracking_data;
    } catch (error) {
        console.error('Error fetching AB tests:', error);
    }
}

function pushExperimentViewToGA(abTests: ABTest[]): void {
    abTests.forEach((abTest) => {
        if (abTest.tracking_enabled) {
            sendExperimentViewEvent(abTest.experiment_key, abTest.variation_id);
        }
    });
}

function saveABTestsToLocalStorage(abTests: ABTest[]): void {
    try {
        localStorage.setItem(AB_TEST_DATA_KEY, JSON.stringify(abTests));
    } catch (error) {
        console.error('Error saving AB tests to localStorage:', error);
    }
}

function loadABTestsFromLocalStorage(): ABTest[] | null {
    try {
        const data = localStorage.getItem(AB_TEST_DATA_KEY);
        if (!data) return null;
        return JSON.parse(data) as ABTest[];
    } catch (error) {
        console.error('Error loading AB tests from localStorage:', error);
        return null;
    }
}

export function getABTests(): ABTest[] {
    return loadABTestsFromLocalStorage() || [];
}

async function initExperimentView(): Promise<void> {
    if (experimentViewSent()) {
        return;
    }

    const abTests = await fetchABTests();

    if (abTests) {
        saveABTestsToLocalStorage(abTests);
        pushExperimentViewToGA(abTests);
    }

    saveSessionData(true);
}

export { initExperimentView };
